import { colors } from '@/utils';

const innerRingOuterRadius = 50;
const innerHoleRadius = 30;
const ringGap = 5;
const outerRingBand = 24;

/**
 * Default geometry and sizing for {@link RadialControlPad}.
 *
 * @example radialPadLayout.ring.innerHoleRadius
 * @example radialPadLayout.icon.box
 */
export const radialPadLayout = {
  /** Maximum sectors in the center void (2 = half-circles, 4 = quarter wedges). */
  maxCenterSectors: 4,
  /** Linear gap between adjacent sector buttons (SVG units; constant width at all radii). */
  sectorGapPx: 4,
  /** Padding around the outermost ring in the SVG canvas. */
  canvasMargin: 2,
  ring: {
    /** Inner void at the center of the inner ring. */
    innerHoleRadius,
    /** Radial space between center sectors and the inner ring (matches inner/outer {@link radialPadLayout.ring.gap}). */
    centerGap: ringGap,
    /** Outer edge of center sectors; stops before the inner ring starts. */
    centerOuterRadius: innerHoleRadius - ringGap,
    /** Outer edge of the inner ring. */
    innerOuterRadius: innerRingOuterRadius,
    /** Radial space between the inner and outer rings. */
    gap: ringGap,
    /** Outer edge of the outer ring. */
    outerOuterRadius: innerRingOuterRadius + ringGap + outerRingBand,
  },
  icon: {
    innerSize: 16,
    outerSize: 14,
    centerSize: 12,
    /** `foreignObject` width/height for centering sector icons in SVG space. */
    box: 32,
    /** Smaller hit area for center half-circle icons. */
    centerBox: 16,
  },
} as const;

/**
 * Radial pad color tokens for overlay on live video.
 * Sector wedges use a single blue fill with opacity per interaction state; icons use white palette tokens.
 *
 * @example radialPadColors.sector.fill.opacity.default
 * @example radialPadColors.sector.icon.hover
 */
export const radialPadColors = {
  /** Circular pad backdrop. */
  pad: {
    background: colors.background.transparent['0'],
  },
  /** Per-wedge path and icon colors. */
  sector: {
    /** `RadialSectorPath` fill: one color; interaction state uses `fill-opacity`. */
    fill: {
      color: colors.palette.blue['700'],
      opacity: {
        default: 0.6,
        hover: 0.8,
        focus: 0.8,
        pressed: 1,
        disabled: 0.3,
      },
    },
    /** Lucide icon stroke on wedges. */
    icon: {
      default: colors.palette.white['900'],
      hover: colors.palette.white['700'],
      pressed: colors.palette.white['1000'],
      disabled: colors.icon.disabled,
    },
  },
} as const;

/**
 * Sector tooltip timing (controlled open; not ScaleUI `delay`).
 *
 * @example radialPadTooltip.showDelayMs
 */
export const radialPadTooltip = {
  /** Delay before tooltips appear after pointer enter (ms). */
  showDelayMs: 500,
  /** How long a visible tooltip stays open before auto-hiding (ms). */
  visibleMs: 1500,
  /** Northward anchor shift (px) by sector direction; tooltip placement is always top. */
  anchorOffsetY: {
    top: 0,
    bottom: 5,
    side: 22,
  },
  /** Gap between anchor and tooltip bubble (floating-ui mainAxis; placement is always top). */
  bubbleGap: 16,
} as const;
